/* eslint-disable class-methods-use-this */
/* eslint-disable complexity */
import React from 'react';
import BaseClass from 'components/BaseClass';
import { wrapComponent } from 'utils/framework';
import {
    Box, Divider, Flex, Text, Grid, Button, Link
} from 'components/ui';
import CreditCard from 'components/FrictionlessCheckout/PaymentReleaseFour/CreditCard';
import OrderUtils from 'utils/Order';
import ErrorConstants from 'utils/ErrorConstants';
import FormValidator from 'utils/FormValidator';
import Radio from 'components/Inputs/Radio/Radio';
import ErrorMsg from 'components/ErrorMsg';
import creditCardUtils from 'utils/CreditCard';
import Debounce from 'utils/Debounce';
import CheckoutUtils from 'utils/Checkout';
import userUtils from 'utils/User';
import ErrorsUtils from 'utils/Errors';
import { breakpoints } from 'style/config';
import { DebouncedResize } from 'constants/events';
import { mediaQueries } from 'style/config';
import deepEqual from 'deep-equal';
import CVCInfoModal from 'components/FrictionlessCheckout/Payment/CVCInfoModal';
import agentAwareUtils from 'utils/AgentAware';
import EditButton from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/CreditCardsList/EditButton';
import RemoveButton from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/CreditCardsList/RemoveButton';

class CreditCardsList extends BaseClass {
    constructor(props) {
        super(props);
        this.state = {
            cvvValidated: false,
            showAllCards: false
        };
    }

    USERCCP = 'userccp';

    checkIsMobile = () => {
        this.setState({
            isMobile: window.matchMedia(breakpoints.smMax).matches
        });
    };

    componentDidMount() {
        const { cards, setNewCreditCard, selectedCreditCardId, alternateMethodSelected } = this.props;

        if (!selectedCreditCardId && !alternateMethodSelected && !CheckoutUtils.isPaymentInOrderComplete()) {
            const selectedCard = cards.filter(creditCard => !!creditCard.isCardInOrder)[0] || cards.filter(creditCard => !!creditCard.isDefault)[0];

            if (selectedCard && creditCardUtils.tokenMigrationDisabledOrSucceed(selectedCard)) {
                setNewCreditCard(selectedCard);
            }
        }

        this.unsubscribe = this.props.CreditCardListToggleCVC(this);

        if (Sephora.isAgent && agentAwareUtils.isAgentAwareSession()) {
            //If it is Sephora Mirror, agent should see place order button disabled. However, certain roles should see enabled button only for card
            const disablePlaceOrderButton = cards?.length ? !agentAwareUtils.isAgentAuthorized(['3']) : true;
            this.props.togglePlaceOrderDisabled(disablePlaceOrderButton);
        }

        this.checkIsMobile();
        this.props.onRef(this);
        window.addEventListener(DebouncedResize, this.checkIsMobile);
    }

    validateCVV = () => {
        ErrorsUtils.collectClientFieldErrors([this.refs.securityCodeInput]);
        ErrorsUtils.validate();
        this.setState({ cvvValidated: true });
    };

    componentDidUpdate(prevProps) {
        if ((!this.state.cvvValidated && this.refs && this.refs.securityCodeInput) || !deepEqual(prevProps.orderDetails, this.props.orderDetails)) {
            this.validateCVV();
        }
    }

    componentWillUnmount() {
        this.unsubscribe();
        window.removeEventListener(DebouncedResize, this.checkIsMobile);
    }

    deleteCreditCard = creditCard => {
        const { creditCardOptions = [] } = this.props;
        const orderId = OrderUtils.getOrderId();
        const nextDefaultCard = creditCardOptions.find(x => !creditCardUtils.tokenMigrationFailed(x) && x.creditCardId !== creditCard.creditCardId);
        const removeCallback = creditCard.isCardInOrder ? this.removeOrderPayment : this.removeCreditCard;

        if (creditCard.isDefault && nextDefaultCard) {
            this.props.setDefaultCreditCardOnProfile(nextDefaultCard.creditCardId).then(() => {
                removeCallback(creditCard.creditCardId, orderId);
            });
        } else {
            removeCallback(creditCard.creditCardId, orderId);
        }
    };

    handleDeleteCreditCard = creditCard => () => {
        this.deleteCreditCard(creditCard);
    };

    showRemoveCreditCardModal = (creditCard, e) => {
        const { removeCreditCard, areYouSureMessage, remove, cancel } = this.props.locales;

        e.preventDefault();

        //variable declaration here for clarity
        const hasCancelButton = true;
        const hasCloseButton = true;
        const callback = () => this.deleteCreditCard(creditCard);

        this.props.showInfoModal({
            isOpen: true,
            title: removeCreditCard,
            message: areYouSureMessage,
            buttonText: remove,
            callback: callback,
            showCancelButton: hasCancelButton,
            cancelText: cancel,
            showCloseButton: hasCloseButton,
            dataAtTitle: 'remove_credit_card_title',
            dataAtButton: 'remove_credit_card_btn',
            dataAtCancelButton: 'remove_credit_card_cancel_btn'
        });
    };

    showRemoveCreditCardModalDebounce = Debounce.preventDoubleClick(this.showRemoveCreditCardModal);

    handleRemoveCreditCard = creditCard => e => {
        this.showRemoveCreditCardModalDebounce(creditCard, e);
    };

    removeOrderPayment = (creditCardId, orderId) => {
        //need to check that credit card being removed is a card in profile
        //if it isn't then we need to set creditCardId to null and just remove
        //the card from the order and not from the users profile
        if (!creditCardUtils.isSavedToProfile(creditCardId)) {
            // eslint-disable-next-line no-param-reassign
            creditCardId = null;
        }

        //if user removes credit card in order but creditCardPaymentGroup.paymentGroupId
        //isn't available, default the creditCardGroupId to 0...
        //this occurs if a user has a selected credit card in order, adds a gift card that
        //covers the entire order, and then removes the credit card that is still in the order
        //but not available in the api response (in order but not selected for order)
        const creditCardGroupId = this.props.creditCardPaymentGroup.paymentGroupId || 0;
        const shipCountry = userUtils.getShippingCountry().countryCode;

        this.props
            .removeOrderPayment(orderId, creditCardGroupId, creditCardId)
            .then(() => {
                //update order details since we removed current paymentGroup
                this.props.getOrderDetails(orderId).then(newOrderDetails => {
                    this.props.updateOrder(newOrderDetails);
                });

                //update users available credit cards
                this.props.getCreditCards(orderId, shipCountry, true).then(paymentOptions => {
                    this.props.merge('order', 'paymentOptions', paymentOptions);
                });
            })
            .catch(errorData => ErrorsUtils.collectAndValidateBackEndErrors(errorData, this));
    };

    removeCreditCard = (creditCardId, orderId) => {
        const profileId = userUtils.getProfileId();
        this.props
            .removeCreditCardFromProfile(profileId, creditCardId)
            .then(() => {
                //update users available credit cards
                this.props.getCreditCards(orderId).then(paymentOptions => {
                    this.props.merge('order', 'paymentOptions', paymentOptions);
                });
            })
            .catch(response => ErrorsUtils.collectAndValidateBackEndErrors(response, this));
    };

    showCVCInfoModal = () => {
        this.props.showCVCInfoModal(true);
    };

    handleSetNewCreditCard = creditCard => () => {
        const { setNewCreditCard, defaultPayment } = this.props;
        setNewCreditCard(creditCard, defaultPayment);
        this.setState({
            cvvValidated: false
        });
    };

    handleShowEditCreditCardForm = creditCard => () => {
        const { showEditCreditCardForm } = this.props;
        showEditCreditCardForm(creditCard);
    };

    handleValidateError = creditCard => code => {
        const { isZeroCheckout } = this.props;

        if (FormValidator.isEmpty(code)) {
            if (isZeroCheckout) {
                return ErrorConstants.ERROR_CODES.CREDIT_CARD_CVV_VALIDATION;
            }

            return ErrorConstants.ERROR_CODES.CREDIT_CARD_SECURITY_CODE;
        } else if (code.length < creditCardUtils.getSecurityCodeLength(creditCard?.cardType)) {
            return ErrorConstants.ERROR_CODES.CREDIT_CARD_SECURITY_CODE_LENGTH;
        }

        return null;
    };

    isUserCcpCard = card => {
        return card.creditCardId?.startsWith(this.USERCCP);
    };

    getCardKey = card => {
        return `${card?.cardType}-${card?.cardNumber}-${card?.expirationMonth}-${card?.expirationYear}`;
    };

    removeDuplicatedCards = cards => {
        const map = new Map();

        cards.forEach(card => {
            const key = this.getCardKey(card);

            if (!map.has(key)) {
                map.set(key, card);

                return;
            }

            const existing = map.get(key);

            // Prefer userccp card (non temporary cards)
            if (!this.isUserCcpCard(existing) && this.isUserCcpCard(card)) {
                map.set(key, card);
            }
        });

        return Array.from(map.values());
    };

    handleUpdateSecurityCode = e => {
        const { updateSecurityCode } = this.props;
        updateSecurityCode(FormValidator.replaceDotSeparator(e.target.value, this.refs.securityCodeInput));
    };

    handleToggleShowAll = () => {
        this.setState(prevState => ({ showAllCards: !prevState.showAllCards }));
    };

    render() {
        const {
            cards, defaultPayment, selectedCreditCardId, shouldDisplayAddOrDeleteCreditCardButton, locales, showEditButtonFirst, onUseThisCard
        } =
            this.props;

        const { showAllCards } = this.state;

        const {
            expiredCreditCardMsg, remove, edit, gotIt, showAll, showLess
        } = locales;

        const creditCardIsDefaultPayment = !defaultPayment || defaultPayment === 'creditCard';

        let showEditRemoveButtons = true;

        if (agentAwareUtils.isAgent) {
            showEditRemoveButtons = agentAwareUtils.isAgentAuthorized(['3']);
        }

        const uniqueCards = this.removeDuplicatedCards(cards);
        const shouldShowToggle = uniqueCards.length > 3;
        const visibleCards = showAllCards || !shouldShowToggle ? uniqueCards : uniqueCards.slice(0, 3);

        return (
            <div>
                {visibleCards.map((creditCard, index) => (
                    <Box
                        data-at={Sephora.debug.dataAt('credit_card_item')}
                        key={creditCard.creditCardId}
                        ref={`creditCard${index}`}
                    >
                        {creditCardUtils.showExpiredMessage(creditCard) && (
                            <ErrorMsg
                                lineHeight='none'
                                marginBottom={1}
                                paddingTop={[2, null]}
                                children={expiredCreditCardMsg}
                            />
                        )}
                        <Grid
                            columns={['1fr', '1fr auto']}
                            paddingTop={4}
                            gap={2}
                            alignItems={['start', 'start']}
                        >
                            <Radio
                                paddingY={0}
                                dotOffset={0}
                                alignItems='center'
                                name='payWithCreditCard'
                                disabled={creditCard.isExpired || creditCardUtils.tokenMigrationFailed(creditCard)}
                                checked={creditCard.creditCardId === selectedCreditCardId}
                                onClick={this.handleSetNewCreditCard(creditCard)}
                                tabIndex={0}
                                css={styles.radio}
                            >
                                <CreditCard
                                    creditCard={creditCard}
                                    showDefault={creditCardIsDefaultPayment && creditCard.isDefault}
                                    isEditView={true}
                                />
                            </Radio>
                            {showEditRemoveButtons && creditCardUtils.tokenMigrationDisabledOrSucceed(creditCard) && (
                                <Flex
                                    alignItems={['center', 'flex-start']}
                                    marginLeft={[9, 0]}
                                    marginTop={[1, 0]}
                                >
                                    {showEditButtonFirst && (
                                        <EditButton
                                            creditCard={creditCard}
                                            editText={edit}
                                            showEditButtonFirst={true}
                                            editCallback={this.handleShowEditCreditCardForm}
                                        />
                                    )}
                                    {shouldDisplayAddOrDeleteCreditCardButton && (
                                        <RemoveButton
                                            creditCard={creditCard}
                                            removeText={remove}
                                            showEditButtonFirst={showEditButtonFirst}
                                            removeCallback={this.handleRemoveCreditCard}
                                        />
                                    )}
                                    {!showEditButtonFirst && (
                                        <EditButton
                                            creditCard={creditCard}
                                            editText={edit}
                                            editCallback={this.handleShowEditCreditCardForm}
                                        />
                                    )}
                                </Flex>
                            )}
                        </Grid>
                        {creditCard.creditCardId === selectedCreditCardId && (
                            <Box
                                marginTop={3}
                                marginBottom={4}
                            >
                                <Button
                                    variant='primary'
                                    width={['100%', '100%', '380px']}
                                    onClick={onUseThisCard}
                                >
                                    {locales.useThisCard}
                                </Button>
                            </Box>
                        )}
                        {creditCardUtils.tokenMigrationFailed(creditCard) && (
                            <Grid
                                columns='1fr auto'
                                fontSize='sm'
                                lineHeight='tight'
                                backgroundColor='nearWhite'
                                alignItems='center'
                                padding={3}
                                borderRadius={2}
                                marginTop={10}
                            >
                                <Text>{creditCard.message}</Text>
                                {showEditRemoveButtons && (
                                    <Button
                                        variant='secondary'
                                        size='xs'
                                        children={gotIt}
                                        onClick={this.handleDeleteCreditCard(creditCard)}
                                    />
                                )}
                            </Grid>
                        )}
                        {Sephora.isAgent && agentAwareUtils.isAgentAwareSession() ? (
                            <>{index !== cards.length - 1 && <Divider marginTop={!showEditRemoveButtons && '1em'} />}</>
                        ) : (
                            <>
                                {!shouldShowToggle ||
                                    (((showAllCards && index !== cards.length - 1) || (!showAllCards && index !== 2)) && <Divider marginTop={4} />)}
                            </>
                        )}
                    </Box>
                ))}
                {shouldShowToggle && (
                    <>
                        <Link
                            color='blue'
                            display='inline-block'
                            marginTop={4}
                            data-at={Sephora.debug.dataAt('show_all_credit_cards')}
                            onClick={this.handleToggleShowAll}
                        >
                            {showAllCards ? showLess : showAll}
                        </Link>
                        <Divider marginTop={4} />
                    </>
                )}
                <CVCInfoModal />
            </div>
        );
    }
}

const styles = {
    message: {
        width: '300px',
        [mediaQueries.sm]: {
            width: '100%'
        },
        [mediaQueries.md]: {
            width: '420px'
        }
    },
    radio: {
        '&:focus-visible': {
            outline: '2px solid #0066cc',
            outlineOffset: '2px'
        }
    }
};

export default wrapComponent(CreditCardsList, 'CreditCardsList');
